import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert, Badge, Button, Card, Collapse, Form, Nav, Spinner } from 'react-bootstrap';
import {
  ArrowClockwise,
  ArrowRight,
  Book,
  CalendarRange,
  Cloud,
  Coin,
  Gear,
  Globe,
  Megaphone,
  Person,
  Rocket,
  Sliders,
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { activityService } from '@/services/activityService';
import { listDeploymentsByTimeRange, type DeploymentRecord } from '@/services/deploymentService';
import { getConfigValue } from '@/services/configService';
import { changedKeys } from '@/components/config/jsonDiff';
import { PageHeader } from '@/components/ui/PageHeader';
import { describeActivity } from '@/utils/activityText';
import { groupDisplayName, isGroupSummary, shortUser, statusWord, type StatusTone } from '@/utils/deployStatus';
import { formatNzDate, formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import type { ActivityFilter, ActivityRecord } from '@/types/activity';
import './Activity.css';

/** How many entries the list shows before "Show more". */
const PAGE = 25;

const RANGES = [
  { value: '1h', label: '1 hour', hours: 1, words: 'the last hour' },
  { value: '1d', label: '1 day', hours: 24, words: 'the last day' },
  { value: '1w', label: '1 week', hours: 24 * 7, words: 'the last week' },
  { value: '1m', label: '1 month', hours: 24 * 30, words: 'the last month' },
] as const;
const DEFAULT_RANGE = '1w';

type Kind = ActivityRecord['type'];

/** The kinds a person can filter by, in the words the list uses. */
const KINDS: { value: Kind; label: string; none: string }[] = [
  { value: 'config', label: 'Config and credits', none: 'config or credit changes' },
  { value: 'deployment', label: 'Deploys', none: 'deploys' },
  { value: 'user', label: 'Portal users', none: 'changes to portal users' },
  { value: 'system', label: 'Everything else', none: 'other activity' },
];

/** What an entry is about. It picks the icon and nothing else. */
type About = 'config' | 'credits' | 'fixed-ip' | 'announcement' | 'docs' | 'account' | 'user' | 'deploy' | 'other';

const ICONS: Record<About, ReactNode> = {
  config: <Sliders />,
  credits: <Coin />,
  'fixed-ip': <Globe />,
  announcement: <Megaphone />,
  docs: <Book />,
  account: <Cloud />,
  user: <Person />,
  deploy: <Rocket />,
  other: <Gear />,
};

interface DiffRow {
  key: string;
  before: string;
  after: string;
}

/** One line of the list, whichever table it came from. */
interface FeedItem {
  id: string;
  about: About;
  timestamp: string;
  title: string;
  detail: string;
  who: string;
  whoFull?: string;
  link?: string;
  badge?: { label: string; tone: StatusTone };
  /** A config change's settings, each with its value before and after. */
  diff: DiffRow[];
}

const startOf = (hours: number) => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();

// ── New Zealand clock, for the two date fields ───────────────────────────────
const NZ_CLOCK = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Pacific/Auckland',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** An instant as "2026-10-13T08:49" on the New Zealand clock: the shape a date-and-time field takes. */
function toNzInput(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  const part: Record<string, string> = {};
  for (const p of NZ_CLOCK.formatToParts(date)) part[p.type] = p.value;
  return `${part.year}-${part.month}-${part.day}T${part.hour}:${part.minute}`;
}

/** The instant a New Zealand clock reading names. Empty when the field is empty or half typed. */
function fromNzInput(value: string): string | undefined {
  if (!value) return undefined;
  const asUtc = Date.parse(`${value}:00Z`);
  if (!Number.isFinite(asUtc)) return undefined;
  // How far the New Zealand clock runs ahead of UTC at a given instant.
  const ahead = (t: number) => Date.parse(`${toNzInput(new Date(t).toISOString())}:00Z`) - t;
  const first = asUtc - ahead(asUtc);
  // A second pass settles a time within a few hours of a daylight saving change.
  return new Date(asUtc - ahead(first)).toISOString();
}

// ── A config change, setting by setting ──────────────────────────────────────
const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const VALUE_WIDTH = 72;
const asText = (v: unknown): string => (v === undefined ? 'not set' : typeof v === 'string' ? v : JSON.stringify(v));

/** Two values cut to one line each. Long ones start just before the first character that differs. */
function cutPair(before: unknown, after: unknown): [string, string] {
  const b = asText(before);
  const a = asText(after);
  if (b.length <= VALUE_WIDTH && a.length <= VALUE_WIDTH) return [b, a];
  let same = 0;
  while (same < b.length && same < a.length && b[same] === a[same]) same += 1;
  const start = Math.max(0, same - 12);
  const cut = (t: string) =>
    `${start > 0 ? '…' : ''}${t.slice(start, start + VALUE_WIDTH)}${t.length > start + VALUE_WIDTH ? '…' : ''}`;
  return [cut(b), cut(a)];
}

/** The settings that differ between two configs. A nested block is opened so its own settings show. */
function diffRows(before: unknown, after: unknown, prefix = '', depth = 0): DiffRow[] {
  if (!isPlainObject(before) || !isPlainObject(after)) return [];
  const rows: DiffRow[] = [];
  for (const key of [...changedKeys(before, after)].sort()) {
    const b = before[key];
    const a = after[key];
    if (depth < 2 && isPlainObject(b) && isPlainObject(a)) {
      rows.push(...diffRows(b, a, `${prefix}${key}.`, depth + 1));
      continue;
    }
    const [shownBefore, shownAfter] = cutPair(b, a);
    rows.push({ key: `${prefix}${key}`, before: shownBefore, after: shownAfter });
  }
  return rows;
}

function aboutOf(record: ActivityRecord): About {
  switch (record.resourceType) {
    case 'client-credits':
      return 'credits';
    case 'fixed-ip':
      return 'fixed-ip';
    case 'announcement':
      return 'announcement';
    case 'support-docs':
      return 'docs';
    case 'aws-account':
      return 'account';
  }
  if (record.type === 'config') return 'config';
  if (record.type === 'user') return 'user';
  if (record.type === 'deployment') return 'deploy';
  return 'other';
}

/**
 * The Configs and Credits pages open on a named client. The shared text gives the page; this
 * adds the client, so the link lands on the thing the entry is about. A deleted client has no
 * page to land on.
 */
function toClient(link: string | undefined, record: ActivityRecord): string | undefined {
  if ((link === '/configs' || link === '/numa-credits') && record.resourceId && record.action !== 'deleted') {
    return `${link}?client=${encodeURIComponent(record.resourceId)}`;
  }
  return link;
}

/** An entry from the portal's own activity record. The words come from the shared helper. */
function fromRecord(record: ActivityRecord): FeedItem {
  const text = describeActivity(record);
  return {
    id: record.activityId,
    about: aboutOf(record),
    timestamp: record.timestamp,
    title: text.title,
    detail: text.detail,
    who: text.who,
    whoFull: record.userEmail,
    link: toClient(text.link, record),
    badge: text.failed ? { label: 'Failed', tone: 'danger' } : undefined,
    diff: diffRows(record.details?.before, record.details?.after),
  };
}

/** An entry from the deploy history. The status words are the ones the Deployments page uses. */
function fromDeploy(dep: DeploymentRecord): FeedItem | null {
  if (!dep.startedAt) return null;
  const word = statusWord(dep.status);
  const group = isGroupSummary(dep);
  const target = group ? `the ${groupDisplayName(dep)} group` : dep.clientName;
  const succeeded = (dep.status || '').toLowerCase() === 'success';
  const plan = dep.mode === 'plan';
  const title = plan
    ? `Planned a deploy to ${target}`
    : word.live
      ? `Started a deploy to ${target}`
      : succeeded
        ? `Deployed to ${target}`
        : `Deploy to ${target}`;
  const detail = [
    dep.imageTag ? `Image ${dep.imageTag}` : '',
    dep.deploymentLabel || '',
    group && dep.clientsTotal ? `${dep.clientsSucceeded ?? 0} of ${dep.clientsTotal} clients succeeded` : '',
    !group && dep.groupName ? `Part of the ${dep.groupName} group run` : '',
    word.tone === 'danger' ? dep.errorTitle || dep.errorSummary || '' : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return {
    id: `deployment-${dep.deploymentId}`,
    about: 'deploy',
    timestamp: dep.startedAt,
    title,
    detail,
    who: shortUser(dep.initiatedBy),
    whoFull: dep.initiatedBy,
    link: group
      ? `/deployments/group/${encodeURIComponent(dep.deploymentId)}`
      : `/deployments/${encodeURIComponent(dep.deploymentId)}/logs`,
    // A deploy that worked says so in its title. Every other outcome gets its word.
    badge: succeeded && !plan ? undefined : { label: word.label, tone: word.tone },
    diff: [],
  };
}

/** The settings a config change touched. Three show; the rest open on request. */
function Changes({ rows, open, onToggle }: { rows: DiffRow[]; open: boolean; onToggle: () => void }) {
  const shown = open ? rows : rows.slice(0, 3);
  return (
    <div className="activity-diff">
      {shown.map((row) => (
        <div key={row.key} className="activity-diff__row">
          <span className="activity-diff__key mono">{row.key}</span>
          <span className="activity-diff__before mono">{row.before}</span>
          <ArrowRight className="activity-diff__arrow" aria-label="changed to" />
          <span className="activity-diff__after mono">{row.after}</span>
        </div>
      ))}
      {rows.length > 3 && (
        <button type="button" className="activity-diff__more" onClick={onToggle}>
          {open ? 'Show fewer' : `Show all ${rows.length} changes`}
        </button>
      )}
    </div>
  );
}

export default function Activity() {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Deploys did not load, so the list holds everything else only. */
  const [deploysMissing, setDeploysMissing] = useState(false);
  const [showDates, setShowDates] = useState(false);
  /** The quick range in use. Empty when the dates were set by hand. */
  const [range, setRange] = useState<string>(DEFAULT_RANGE);
  const [limit, setLimit] = useState(PAGE);
  /** Entries whose full list of changed settings is open. */
  const [openDiffs, setOpenDiffs] = useState<Set<string>>(new Set());
  const deploymentsTable = getConfigValue('DEPLOYMENTS_TABLE');

  // Filter state with default 1 week range
  const [filters, setFilters] = useState<ActivityFilter>(() => ({ startTime: startOf(24 * 7) }));

  // Counts loads, so a slow answer to an old filter never replaces the answer to the new one.
  const loadCount = useRef(0);
  const loadActivities = useCallback(async () => {
    const mine = ++loadCount.current;
    try {
      setLoading(true);
      setError(null);

      const wantDeploys = Boolean(deploymentsTable) && (!filters.type || filters.type === 'deployment');
      let deploysFailed = false;

      // Fetch both activity records and deployment records
      const [activityRecords, deploymentRecords] = await Promise.all([
        activityService.getRecentActivities({
          ...filters,
          // With no kind picked the service shares its limit across the four kinds, so ask for
          // four times as many: enough for the newest entries of any one kind.
          limit: filters.type ? limit : limit * 4,
        }),
        wantDeploys
          ? listDeploymentsByTimeRange({
              fromIso: filters.startTime ?? startOf(24 * 30),
              toIso: filters.endTime,
              pageSize: limit,
            })
              .then((res) => res.items)
              .catch((e) => {
                console.error('Deploys did not load for the activity list', e);
                deploysFailed = true;
                return [] as DeploymentRecord[];
              })
          : Promise.resolve([] as DeploymentRecord[]),
      ]);
      if (mine !== loadCount.current) return;

      // Keep to the dates asked for, whichever table an entry came from.
      const from = filters.startTime ? Date.parse(filters.startTime) : -Infinity;
      const to = filters.endTime ? Date.parse(filters.endTime) : Infinity;

      // Combine and sort all activities
      const merged = [
        ...activityRecords.map(fromRecord),
        ...deploymentRecords.map(fromDeploy).filter((item): item is FeedItem => item !== null),
      ]
        .filter((item) => {
          const at = Date.parse(item.timestamp);
          return Number.isFinite(at) && at >= from && at <= to;
        })
        .sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp));

      setItems(merged.slice(0, limit));
      setMore(merged.length >= limit);
      setDeploysMissing(deploysFailed);
    } catch (err) {
      if (mine !== loadCount.current) return;
      setError(err instanceof Error ? err.message : 'Unknown error');
      setItems([]);
      setMore(false);
    } finally {
      if (mine === loadCount.current) setLoading(false);
    }
  }, [filters, limit, deploymentsTable]);

  useEffect(() => {
    void loadActivities();
  }, [loadActivities]);

  const handleFilterChange = (patch: Partial<ActivityFilter>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setLimit(PAGE);
  };

  const handleTimeRangeChange = (rangeValue: string) => {
    const picked = RANGES.find((r) => r.value === rangeValue);
    if (!picked) return;
    setRange(rangeValue);
    setFilters((prev) => ({
      ...prev,
      startTime: startOf(picked.hours),
      endTime: undefined, // Clear end time when using quick ranges
    }));
    setLimit(PAGE);
  };

  /** A date typed by hand takes over from the quick range. */
  const handleDateChange = (key: 'startTime' | 'endTime', value: string) => {
    setRange('');
    const at = fromNzInput(value);
    handleFilterChange(key === 'startTime' ? { startTime: at } : { endTime: at });
  };

  const clearFilters = () => {
    setFilters({ startTime: startOf(24 * 7) }); // Keep 1 week default
    setRange(DEFAULT_RANGE);
    setLimit(PAGE);
  };

  const isDefault = range === DEFAULT_RANGE && !filters.type && !filters.endTime;
  const rangeWords = RANGES.find((r) => r.value === range)?.words ?? 'those dates';
  const kind = KINDS.find((k) => k.value === filters.type);
  const emptyText = kind ? `No ${kind.none} in ${rangeWords}.` : `Nothing was recorded in ${rangeWords}.`;

  // One block for each New Zealand day, newest first.
  const days = useMemo(() => {
    const today = formatNzDate(new Date());
    const yesterday = formatNzDate(new Date(Date.now() - 24 * 60 * 60 * 1000));
    const blocks: { label: string; items: FeedItem[] }[] = [];
    for (const item of items) {
      const date = formatNzDate(item.timestamp);
      const label = date === today ? 'Today' : date === yesterday ? 'Yesterday' : date;
      const last = blocks[blocks.length - 1];
      if (last && last.label === label) last.items.push(item);
      else blocks.push({ label, items: [item] });
    }
    return blocks;
  }, [items]);

  const toggleDiff = (id: string) =>
    setOpenDiffs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="activity-page">
      <PageHeader
        title="Activity"
        actions={
          <Button variant="outline-secondary" disabled={loading} onClick={() => void loadActivities()}>
            {loading ? <Spinner animation="border" size="sm" className="me-2" /> : <ArrowClockwise className="me-1" />}
            Refresh
          </Button>
        }
      />

      {/* ── How far back, which dates, which kind ── */}
      <div className="activity-toolbar">
        <Nav
          variant="pills"
          className="nav-pills-custom"
          activeKey={range}
          onSelect={(key) => key && handleTimeRangeChange(key)}
          aria-label="How far back"
        >
          {RANGES.map((r) => (
            <Nav.Item key={r.value}>
              <Nav.Link eventKey={r.value}>{r.label}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <Button
          variant="outline-secondary"
          className={range === '' ? 'activity-toolbar__dates is-on' : 'activity-toolbar__dates'}
          aria-expanded={showDates}
          aria-controls="activity-dates"
          onClick={() => setShowDates(!showDates)}
        >
          <CalendarRange className="me-1" />
          {range === '' ? 'Your dates' : 'Pick dates'}
        </Button>
        <Form.Select
          className="activity-toolbar__kind"
          aria-label="Kind of activity"
          value={filters.type || ''}
          onChange={(e) => handleFilterChange({ type: (e.target.value || undefined) as ActivityFilter['type'] })}
        >
          <option value="">All activity</option>
          {KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </Form.Select>
        {!isDefault && (
          <Button variant="link" className="activity-toolbar__reset" onClick={clearFilters}>
            Reset filters
          </Button>
        )}
        {!loading && items.length > 0 && (
          <span className="activity-toolbar__count">
            {more
              ? `The newest ${items.length} entries`
              : `${items.length} ${items.length === 1 ? 'entry' : 'entries'}`}
          </span>
        )}
      </div>
      <Collapse in={showDates}>
        <div id="activity-dates">
          <div className="activity-dates">
            <div>
              <Form.Label htmlFor="activity-from">From, New Zealand time</Form.Label>
              <Form.Control
                id="activity-from"
                type="datetime-local"
                value={toNzInput(filters.startTime)}
                onChange={(e) => handleDateChange('startTime', e.target.value)}
              />
            </div>
            <div>
              <Form.Label htmlFor="activity-to">To, New Zealand time</Form.Label>
              <Form.Control
                id="activity-to"
                type="datetime-local"
                value={toNzInput(filters.endTime)}
                onChange={(e) => handleDateChange('endTime', e.target.value)}
              />
              <Form.Text>Leave empty for up to now.</Form.Text>
            </div>
          </div>
        </div>
      </Collapse>

      {error && (
        <Alert variant="danger" className="activity-alert">
          <span>The activity list did not load: {error}</span>
          <Button variant="outline-danger" size="sm" onClick={() => void loadActivities()}>
            Try again
          </Button>
        </Alert>
      )}
      {deploysMissing && (
        <Alert variant="warning" className="activity-alert">
          <span>Deploys did not load, so this list leaves them out.</span>
          <Button variant="outline-secondary" size="sm" onClick={() => void loadActivities()}>
            Try again
          </Button>
        </Alert>
      )}

      {loading && items.length === 0 && (
        <Card>
          <div className="activity-empty">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading activity
          </div>
        </Card>
      )}

      {!loading && !error && items.length === 0 && (
        <Card>
          <div className="activity-empty">
            {emptyText}
            {!isDefault && (
              <Button variant="link" className="activity-toolbar__reset" onClick={clearFilters}>
                Reset filters
              </Button>
            )}
          </div>
        </Card>
      )}

      {days.map((day) => (
        <section key={day.label} className="activity-day">
          <h2 className="section-label">{day.label}</h2>
          <Card>
            <ul className="activity-list">
              {day.items.map((item) => (
                <li key={item.id} className="activity-row">
                  <span
                    className={`activity-row__icon${item.badge?.tone === 'danger' ? ' activity-row__icon--failed' : ''}`}
                    aria-hidden="true"
                  >
                    {ICONS[item.about]}
                  </span>
                  <div className="activity-row__main">
                    <div className="activity-row__title">
                      {item.link ? <Link to={item.link}>{item.title}</Link> : item.title}
                      {item.badge && <Badge bg={item.badge.tone}>{item.badge.label}</Badge>}
                    </div>
                    {item.diff.length > 0 ? (
                      <Changes rows={item.diff} open={openDiffs.has(item.id)} onToggle={() => toggleDiff(item.id)} />
                    ) : (
                      item.detail && <div className="activity-row__detail">{item.detail}</div>
                    )}
                    <div className="activity-row__meta">
                      {item.who && <span title={item.whoFull}>{item.who}</span>}
                      <time dateTime={item.timestamp}>{timeAgo(item.timestamp)}</time>
                      <span>{formatNzDateTime(item.timestamp)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </section>
      ))}

      {more && items.length > 0 && (
        <div className="activity-more">
          <Button variant="outline-secondary" disabled={loading} onClick={() => setLimit((n) => n + PAGE)}>
            {loading ? 'Loading' : `Show ${PAGE} more`}
          </Button>
        </div>
      )}
    </div>
  );
}
